import { useState, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import InfiniteScroll from 'react-infinite-scroll-component'
import { flatten, max, intersection } from 'lodash-es'
import styled from '@emotion/styled'

import { selectWebview } from 'store/webview/webviewSlice'
import {
  useAlbums,
  useAlbumCount,
  useInfiniteAlbums,
  usePhotoClassifications,
  usePhotoTags,
} from 'utils/api/hooks'
import { useAlbumModal } from 'utils/hooks'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import { LS_APP_DOMAIN } from 'config/constants'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import Footer from 'components/common/Footer'
import ContentWrapper from 'components/common/ContentWrapper'
import NowrapDiv from 'components/common/NowrapDiv'
import { FullscreenModal } from 'components/common/Modal'
import AlbumListItem from 'components/common/AlbumListItem'
import Pagination from 'components/common/Pagination'
import FilterModal from './FilterModal'
import FiltersChangeModal from './FiltersChangeModal'
import SortSelect from './SortSelect'
import TagPopupSelect from './TagPopupSelect'
import { getFiltersNameString } from './helpers'
import ScrollableWrapper from 'components/common/ScrollableWrapper'
import { GtmAgent } from 'utils/hooks/analytics'

const DynamicAlbumContent = dynamic(() =>
  import('components/pages/Album/AlbumContent')
)

export const NUMBER_PER_PAGE = 30
export const DEFAULT_SORT = '-sort -createdTime'

const FilterButton = styled(Button)`
  ${({ theme }) => `
    & {
      justify-content: flex-start;
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-weight: ${theme.typography.fontWeightRegular};
    }
  `}
`

const Img = styled.img`
  width: 236px;
  max-width: 100%;
`

const AlbumIndex = ({ isMobile, fallbackData }) => {
  const [searchText, setSearchText] = useState(fallbackData?.searchText || '')
  const [order, setOrder] = useState(DEFAULT_SORT)
  const [filters, setFilters] = useState(fallbackData?.filters || [])
  const [page, setPage] = useState(fallbackData?.page || 1)
  const onRemoveFilters = () => {
    setFilters([])
    setPage(1)
  }
  const onFilterChange = (newFilters) => {
    setFilters(newFilters)
    setPage(1)
  }

  const router = useRouter()
  const venderId = router.query?.venderId
  // 讓query string跟著頁碼變動
  useEffect(() => {
    const qs = window.location.href.split('?')?.[1] || ''
    const pageInQs = qs
      .split('&')
      .filter((string) => /^page=/.test(string))
      .map((string) => string.split('=')[1] || '')
      .shift()
    if (parseInt(pageInQs) !== page) {
      const newQs = qs
        .split('&')
        .filter((string) => string && !/^page=/.test(string))
        .concat([`page=${page}`])
        .join('&')
      window.history.replaceState(window.history.state, '', `?${newQs}`)
    }
  }, [page])

  // 讓query string跟著篩選變動
  useEffect(() => {
    const qs = window.location.href.split('?')?.[1] || ''
    const tagsInQs = qs
      .split('&')
      .filter((string) => /^tag=/.test(string))
      .map((string) => string.split('=')[1] || '')
    const matchTags = intersection(filters, tagsInQs)
    if (
      matchTags?.length !== filters?.length ||
      matchTags?.length !== tagsInQs?.length
    ) {
      const tagQsArray = filters.map((filter) => `tag=${filter}`)
      const newQs = qs
        .split('&')
        .filter((string) => string && !/^tag=/.test(string))
        .concat(tagQsArray)
        .join('&')
      window.history.replaceState(window.history.state, '', `?${newQs}`)
    }
  }, [filters])

  // 當query變動時，檢查是否需要調整篩選條件
  useEffect(() => {
    if (router.isReady) {
      const tagsFromQuery =
        typeof router.query?.tag === 'string'
          ? [router.query.tag]
          : router.query?.tag || []
      const matchedFilters = intersection(tagsFromQuery, filters)
      const matchedLength = matchedFilters?.length
      if (
        matchedLength !== filters?.length ||
        matchedLength !== tagsFromQuery?.length
      ) {
        setFilters(tagsFromQuery)
      }
    }
  }, [router.isReady, router.query?.tag])

  // 當fallbackData.searchText變化時表示發生page route，
  // 此時將頁碼設回1
  useEffect(() => {
    if (fallbackData?.searchText !== searchText) {
      setSearchText(fallbackData.searchText)
      setPage(1)
    }
  }, [fallbackData?.searchText])

  // 頁碼變動時判斷點擊換頁，scroll至節點頂部
  useEffect(() => {
    setTimeout(
      () =>
        containerRef.current &&
        containerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        }),
      0
    )
  }, [page])

  const { data: tagCategories } = usePhotoClassifications(
    {},
    { fallbackData: fallbackData?.photoClassifications }
  )

  const {
    data: paginatedAlbums,
    isLoading: isPaginatedAlbumsLoading,
  } = useAlbums(
    () => {
      if (isMobile) {
        return null
      }
      const query = {
        skip: max([page - 1, 0]) * NUMBER_PER_PAGE,
        limit: NUMBER_PER_PAGE,
        sorts: order.split(' '),
      }
      if (venderId) {
        query.venderId = venderId
      }
      if (searchText) {
        return { ...query, search: searchText }
      }
      return filters?.length ? { ...query, tagUrlIds: filters } : query
    },
    { fallbackData: fallbackData?.albums }
  )
  const { data: albumCount } = useAlbumCount(
    () => {
      if (isMobile) {
        return null
      }

      const query = {}

      if (venderId) {
        query.venderId = venderId
      }

      if (searchText) {
        return { ...query, search: searchText }
      }

      return filters?.length ? { ...query, tagUrlIds: filters } : query
    },
    { fallbackData: fallbackData?.albumCount }
  )

  const {
    data: infiniteAlbums,
    isLoading: isInfiniteAlbumsLoading,
    size,
    setSize,
  } = useInfiniteAlbums(
    () => {
      if (!isMobile) {
        return null
      }
      const query = { sorts: order.split(' '), limit: NUMBER_PER_PAGE }
      if (venderId) {
        query.venderId = venderId
      }
      if (searchText) {
        return { ...query, search: searchText }
      }
      return filters?.length ? { ...query, tagUrlIds: filters } : query
    },
    { fallbackData: [fallbackData?.albums] }
  )
  const isAlbumsLoading = isPaginatedAlbumsLoading || isInfiniteAlbumsLoading
  const albums = flatten(isMobile ? infiniteAlbums : paginatedAlbums)
  const onLoadMore = () => setSize(size + 1)

  const { data: photoTags } = usePhotoTags(
    () => (searchText ? { tagName: searchText } : null),
    { fallbackData: fallbackData?.photoTags }
  )

  const [filterModal, setFilterModal] = useState({ open: false })
  const openFilterModal = () => setFilterModal({ open: true })
  const closeFilterModal = () => setFilterModal({ open: false })

  const webview = useSelector(selectWebview)
  const invokeNativeHeaderControls = () => {
    try {
      enableNativeHeaderMainTabs(true)
      enableNativeHeaderNavBack(false)
      enableNativeHeaderSearch(true)
    } catch (err) {
      console.error('native notifier error: ', err.message)
    }
  }
  useEffect(() => {
    if (webview?.name === 'lsApp') {
      invokeNativeHeaderControls()
    }
  }, [webview])

  // album content modal
  const {
    albumModal,
    closeAlbumModal,
    onAlbumClick,
    onAlbumContentClose,
  } = useAlbumModal('/album', router, {
    webview,
    nativeHeaderHandler: invokeNativeHeaderControls,
  })

  const containerRef = useRef(null)
  const onAlbumTagClick = (tag) => {
    if (filters?.length < 2) {
      router.push(`/album?tag=${tag.urlId}`)

      if (containerRef?.current) {
        containerRef.current?.scrollTo(0, 0)
      }
    } else {
      setFiltersChangeModal({ open: true, tag })
    }
  }
  const [filtersChangeModal, setFiltersChangeModal] = useState({ open: false })
  const onFiltersChangeModalConfirm = (tag) => {
    router.push(`/album?tag=${tag.urlId}`)
    setFiltersChangeModal({ open: false })
  }

  const containerId = 'scrollable-album-container'

  return (
    <Layout stickyHeader={<Header />} fixContentHeight>
      <ScrollableWrapper
        height="100"
        overflow="hidden"
        containerId={containerId}
        wrapperRef={containerRef}
      >
        <ContentWrapper>
          {isMobile ? (
            <>
              <Box
                display="flex"
                flexWrap="nowrap"
                alignItems="center"
                overflow="hidden"
                my={4}
              >
                <Box mr={3} whiteSpace="nowrap">
                  <Typography color="textSecondary">篩選</Typography>
                </Box>
                {filters?.length > 0 ? (
                  <>
                    <Box mr={3} overflow="hidden">
                      <FilterButton
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={openFilterModal}
                      >
                        {getFiltersNameString(tagCategories, filters)}
                      </FilterButton>
                    </Box>
                    <Button variant="outlined" size="small">
                      <Typography color="error" onClick={onRemoveFilters}>
                        刪除
                      </Typography>
                    </Button>
                  </>
                ) : (
                  <Box display="flex" flexWrap="wrap" height="45px">
                    {tagCategories?.map((category) => (
                      <Box mr={3} key={category._id}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openFilterModal}
                        >
                          <Typography>{category.name}</Typography>
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box mb={6}>
                <SortSelect
                  color={isMobile ? 'text' : 'primary'}
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                />
              </Box>
            </>
          ) : (
            <Box my={6}>
              <Grid container>
                <Grid container item xs={9} spacing={3} alignItems="center">
                  {!!searchText ? (
                    <Box width="100%">
                      <Typography variant="h4">
                        <NowrapDiv>搜尋內容：{searchText}</NowrapDiv>
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Grid item>
                        <Typography color="textSecondary">篩選</Typography>
                      </Grid>
                      {tagCategories?.map((category) => (
                        <Grid item key={category._id}>
                          <TagPopupSelect
                            category={category}
                            filters={filters}
                            setFilters={onFilterChange}
                          />
                        </Grid>
                      ))}
                      {!!filters?.length && (
                        <Grid item>
                          <Typography
                            color="error"
                            sx={{ cursor: 'pointer' }}
                            onClick={onRemoveFilters}
                          >
                            清除條件
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={3}>
                  <Box display="flex" justifyContent="flex-end">
                    <SortSelect
                      color={isMobile ? 'text' : 'primary'}
                      value={order}
                      onChange={(e) => setOrder(e.target.value)}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box display="flex" mt={6}>
                <Box flexGrow={1}>
                  {(!!filters?.length || !!searchText) && (
                    <Typography variant="h6">
                      共篩出
                      <Typography variant="h6" component="span" color="primary">
                        <Box component="span" fontWeight="fontWeightBold">
                          {` ${albumCount ?? ''} `}
                        </Box>
                      </Typography>
                      個相簿
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    pageAmount={Math.floor(albumCount / NUMBER_PER_PAGE) + 1}
                    urlPath={LS_APP_DOMAIN + '/album'}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {!isAlbumsLoading && !albums?.length ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt={25}
            >
              <Img src="/img/default-search-off.svg" />
              <Box mt={5} mb={3}>
                <Typography variant="h6">空的，沒搜尋到任何東西！</Typography>
              </Box>
              <Box color="text.secondary">
                {!!filters?.length ? '請重新設定標籤' : '請重新輸入關鍵字'}
              </Box>
            </Box>
          ) : (
            <InfiniteScroll
              dataLength={albums?.length || 0}
              hasMore={true}
              next={onLoadMore}
              scrollableTarget={containerId}
            >
              <Box overflow="hidden">
                <Grid container spacing={isMobile ? 2 : 6}>
                  {albums?.map((album) => (
                    <Grid item xs={6} lg={4} key={album._id}>
                      <AlbumListItem
                        album={album}
                        onClick={(event) => {
                          if (album) {
                            GtmAgent.directSendToGTM({
                              event: 'living_action',
                              living_event_name: 'booking_service_photoalbum',
                              living_click_name: album?.name,
                              living_click_location: '相簿圖輯列表頁',
                              living_vender: album?.vender?.name,
                              living_click_photoalbum_id: album?._id,
                            })
                          }
                          onAlbumClick(event, album)
                        }}
                        onAlbumTagClick={onAlbumTagClick}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </InfiniteScroll>
          )}
        </ContentWrapper>

        {!isMobile && (
          <Box display="flex" justifyContent="center" mt={6} mb={14}>
            <Pagination
              page={page}
              setPage={setPage}
              pageAmount={Math.floor(albumCount / NUMBER_PER_PAGE) + 1}
            />
          </Box>
        )}

        {webview?.name !== 'lsApp' && <Footer />}
      </ScrollableWrapper>

      <FilterModal
        open={filterModal.open}
        onClose={closeFilterModal}
        onConfirm={onFilterChange}
        defaultFilters={filters}
        tagCategories={tagCategories}
      />

      <FiltersChangeModal
        open={filtersChangeModal.open}
        onClose={() => setFiltersChangeModal({ open: false })}
        onConfirm={onFiltersChangeModalConfirm}
        originFilters={filters}
        tag={filtersChangeModal.tag}
        tagCategories={tagCategories}
      />

      {router?.isReady && albumModal.open && (
        <FullscreenModal open={albumModal.open} onClose={closeAlbumModal}>
          <DynamicAlbumContent
            onClose={onAlbumContentClose}
            closeModal={closeAlbumModal}
            fallbackData={albumModal.fallbackData}
          />
        </FullscreenModal>
      )}
    </Layout>
  )
}

export default AlbumIndex
